import React, { useCallback } from 'react';
import * as yup from 'yup';
import { useStringsContext } from '../../../../contexts/strings';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TextInputWithValidate, {
  InputWithValidateProps,
} from '../../../common/input/text-input-with-validate';
import Checkbox from '@mui/material/Checkbox';
import { SxStyle } from '../../../../types';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

const styles: SxStyle = {
  dragging: {
    border: '1px dashed',
    borderColor: '#b4bbc8',
    borderRadius: 1,
    backgroundColor: 'white',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  active: {
    opacity: 0,
  },
  container: {
    display: 'flex',
    maxHeight: '56px',
    alignItems: 'center',
  },
};

export interface PollAlternativeItemProps extends InputWithValidateProps {
  name: string;
  label: string;
  value?: string;
  disabled?: boolean;
  onValue?: (key: string, value: string | null) => void;
  onDelete?: () => void;
  isDragging?: boolean;
  isActive?: boolean;
  listeners?: any;
  onCheck?: (checked: boolean) => void;
  checked?: boolean;
}

const PollAlternativeItem = ({
  name,
  label,
  value,
  disabled,
  onValue,
  onDelete,
  onValidation,
  hideValidationError,
  listeners,
  isDragging,
  isActive,
  onCheck,
  checked,
}: PollAlternativeItemProps) => {
  const { strings } = useStringsContext();

  const styling = {
    ...(isActive ? styles.active : {}),
    ...(isDragging ? styles.dragging : {}),
  };

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onCheck && onCheck(checked);
    },
    [onCheck]
  );

  return (
    <Stack direction="row" spacing={1} sx={styling}>
      <Stack sx={styles.container} direction="row">
        <IconButton sx={{ width: '32px' }} {...listeners}>
          <DragIndicatorIcon />
        </IconButton>
        {onCheck && (
          <Tooltip placement="top" arrow title={<div>{strings.polls.toolTipAlternative}</div>}>
            <Checkbox
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<CheckCircleIcon color="success" />}
              onChange={onChange}
              checked={checked}
              sx={{ width: '40px' }}
            />
          </Tooltip>
        )}
      </Stack>
      <TextInputWithValidate
        name={name}
        validation={yup.string().required(strings.validations.isRequired)}
        label={label}
        value={value}
        onValue={onValue}
        onValidation={onValidation}
        hideValidationError={hideValidationError}
      />
      <Box sx={styles.container}>
        <IconButton sx={{ width: '40px' }} onClick={onDelete} disabled={disabled}>
          <DeleteIcon color={disabled ? 'disabled' : 'error'} />
        </IconButton>
      </Box>
    </Stack>
  );
};

export default PollAlternativeItem;
